import React from "react";
import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import type {
  LanguageSwitcherProps,
  ThemeOption,
  ThemeToggleProps,
  UiThemeMode,
} from "./types";

const DEFAULT_THEME_OPTIONS: Array<ThemeOption & { icon: React.ComponentType<{ className?: string }> }> = [
  { value: "light", icon: SunIcon },
  { value: "dark", icon: MoonIcon },
  { value: "system", icon: ComputerDesktopIcon },
];

function mergeThemeOptions(options?: ThemeOption[]) {
  return DEFAULT_THEME_OPTIONS.map((defaultOption) => {
    const override = options?.find((item) => item.value === defaultOption.value);
    return { ...defaultOption, ...override };
  });
}

export function ThemeToggle({
  compact = false,
  mode,
  setMode,
  options,
}: ThemeToggleProps) {
  const resolvedOptions = mergeThemeOptions(options);

  if (compact) {
    const currentIndex = resolvedOptions.findIndex((item) => item.value === mode);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const current = resolvedOptions[safeIndex];
    const next = resolvedOptions[(safeIndex + 1) % resolvedOptions.length];
    const Icon = current.icon;

    return (
      <button
        onClick={() => setMode(next.value as UiThemeMode)}
        className="w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-50 hover:bg-bg-elevated transition-all duration-200 cursor-pointer"
        title={current.label ?? current.value}
      >
        <Icon className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 p-1 rounded-lg bg-bg-elevated">
      {resolvedOptions.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          onClick={() => setMode(value as UiThemeMode)}
          title={label ?? value}
          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 cursor-pointer ${
            mode === value
              ? "bg-bg-surface text-gray-50 shadow-sm"
              : "text-gray-400 hover:text-gray-200"
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export function LanguageSwitcher({
  compact = false,
  locale,
  setLocale,
  locales,
}: LanguageSwitcherProps) {
  if (locales.length === 0) return null;

  if (compact) {
    const currentIndex = locales.findIndex((item) => item.value === locale);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const current = locales[safeIndex];
    const next = locales[(safeIndex + 1) % locales.length];

    return (
      <button
        onClick={() => setLocale(next.value)}
        title={`当前：${current.fullLabel}，点击切换`}
        className="w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-50 hover:bg-bg-elevated transition-all duration-200 cursor-pointer"
      >
        <span className="text-xs font-semibold">{current.shortLabel}</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 p-1 rounded-lg bg-bg-elevated">
      {locales.map((item) => (
        <button
          key={item.value}
          onClick={() => setLocale(item.value)}
          title={item.fullLabel}
          className={`flex-1 flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 cursor-pointer ${
            locale === item.value
              ? "bg-bg-surface text-gray-50 shadow-sm"
              : "text-gray-400 hover:text-gray-200"
          }`}
        >
          {item.fullLabel}
        </button>
      ))}
    </div>
  );
}
